import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, HardHat, Loader2, Send } from "lucide-react";
import { fmtD } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TaskLinkPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [sched, setSched] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [lnote, setLnote] = useState("");
  const [showChanges, setShowChanges] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/public/task-link/${token}`);
      setD(data);
    } catch (e) { setErr(e?.detailText || "链接无效"); }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [d?.msgs?.length]);

  const upload = async (fl) => {
    setUpBusy(true);
    const added = [];
    for (const f of Array.from(fl).slice(0, 6)) {
      if (f.size > 20 * 1024 * 1024) { setErr(`「${f.name}」超过 20MB 上限，已跳过`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${BACKEND}/api/public/task-link/${token}/upload`, fd);
        added.push(data);
      } catch (e) { setErr(e?.detailText || "上传失败"); }
    }
    setFiles((p) => [...p, ...added]);
    setUpBusy(false);
  };

  const submit = async (action, text, extra = {}) => {
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${BACKEND}/api/public/task-link/${token}/submit`, { action, note: text, files, ...extra });
      setNote(""); setMsg(""); setFiles([]); setSched("");
      setOkMsg(action === "done" ? "已提交，项目经理会尽快审核确认" : "已提交，项目经理已收到提醒");
      setTimeout(() => setOkMsg(""), 5000);
      await load();
    } catch (e) { setErr(e?.detailText || "提交失败"); }
    setBusy(false);
  };

  if (err && !d) return <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6"><p className="text-sm text-slate-500" data-testid="tasklink-error">{err}</p></div>;
  if (!d) return <div className="min-h-screen bg-slate-100 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const isPunch = d.kind === "punch";
  const isLayout = (d.kind || "").startsWith("layout");
  const isOwnerLayout = d.kind === "layout_owner";
  const canActLayout = !d.expired && d.layout && ((d.kind === "layout_vendor" && d.layout.status === "vendor_review") || (isOwnerLayout && d.layout.status === "owner_review"));
  const layoutAction = async (action) => {
    setBusy(true);
    setErr("");
    try {
      await axios.post(`${BACKEND}/api/public/task-link/${token}/layout-action`, { action, note: lnote });
      setLnote(""); setShowChanges(false);
      setOkMsg(action === "approve" ? "已确认，项目团队已收到通知" : "修改意见已提交，项目团队已收到通知");
      setTimeout(() => setOkMsg(""), 5000);
      await load();
    } catch (e) { setErr(e?.detailText || "提交失败"); }
    setBusy(false);
  };
  const doneStates = ["done", "closed", "submitted"];
  return (
    <div className="min-h-screen bg-slate-100 pb-10" data-testid="tasklink-page">
      <div className="bg-slate-900 text-white px-4 py-4">
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5" />ALIGCUS {isLayout ? (isOwnerLayout ? "橱柜布局最终确认" : "橱柜布局审核") : isPunch ? "整改任务" : "工程任务"}</p>
        <h1 className="text-base font-bold mt-1" data-testid="tasklink-title" data-keep-title>{d.title}</h1>
        <p className="text-xs text-slate-300 mt-0.5">{d.address} · {d.subName}</p>
      </div>
      <div className="max-w-lg mx-auto px-3 mt-3 space-y-3">
        {d.expired && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700" data-testid="tasklink-expired">链接已过期，仅可查看历史记录。如需继续提交，请联系项目经理重新发送。</div>}
        {err && <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-600" data-testid="tasklink-err">{err}</div>}
        {okMsg && <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-xs text-emerald-700 font-semibold" data-testid="tasklink-ok">✓ {okMsg}</div>}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          {d.duty && <p className="text-xs text-slate-500 leading-relaxed">{d.duty}</p>}
          {d.evidenceRequired && <p className="text-[11px] text-amber-600 font-semibold mt-1.5">📎 本任务需上传照片/文件作为完成证据</p>}
          {doneStates.includes(d.refStatus) && (
            <p className="text-[11px] text-emerald-600 font-bold mt-1.5 flex items-center gap-1" data-testid="tasklink-status-done"><CheckCircle2 className="h-3.5 w-3.5" />{d.refStatus === "submitted" ? "已提交，等待项目经理审核" : "该任务已确认完成"}</p>
          )}
          {(d.evidence || []).length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <p className="text-[10px] text-slate-400 font-semibold mb-1">已提交的证据</p>
              {d.evidence.map((e, i) => (
                <p key={i} className="text-[11px] text-slate-600 truncate" data-testid={`tasklink-evidence-${i}`}>📄 {e.filename} <span className="text-slate-300">· {e.by} · {fmtD(e.at)}</span></p>
              ))}
            </div>
          )}
        </div>

        {isLayout && d.layout && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4" data-testid="tasklink-layout-card">
            <p className="text-xs font-bold text-slate-700 mb-2">布局图纸 Layout Drawings</p>
            <div className="space-y-2">
              {(d.layout.files || []).map((f, i) => (
                (f.contentType || "").startsWith("image/")
                  ? <img key={i} src={`${BACKEND}/api/public/task-link/${token}/file/${f.fileId}`} alt={f.filename} className="rounded-xl border border-slate-100 w-full" data-testid={`tasklink-layout-img-${i}`} />
                  : <a key={i} href={`${BACKEND}/api/public/task-link/${token}/file/${f.fileId}`} target="_blank" rel="noreferrer" className="block text-xs text-indigo-600 underline" data-testid={`tasklink-layout-file-${i}`}>📄 {f.filename}</a>
              ))}
            </div>
            {d.layout.note && <p className="text-[11px] text-slate-500 mt-2">PM 备注: {d.layout.note}</p>}
            {d.layout.vendorNote && <p className="text-[11px] text-amber-600 mt-1">供应商意见: {d.layout.vendorNote}</p>}
            {d.layout.ownerNote && <p className="text-[11px] text-amber-600 mt-1">业主意见: {d.layout.ownerNote}</p>}
            {d.layout.status === "approved" && <p className="text-[11px] font-bold text-emerald-600 mt-2" data-testid="tasklink-layout-approved">✓ 布局已最终确认，项目团队将按图执行</p>}
            {d.layout.status === "changes_requested" && <p className="text-[11px] font-bold text-amber-600 mt-2" data-testid="tasklink-layout-changed">⚠ 已要求修改，等待项目团队更新图纸</p>}
            {isOwnerLayout && d.layout.status === "owner_review" && <p className="text-[10px] text-slate-400 mt-1.5">供应商 {d.layout.vendorName} 已审核通过，请您最终确认。</p>}
            {canActLayout && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <button data-testid="tasklink-layout-approve" disabled={busy} onClick={() => layoutAction("approve")}
                    className="flex-1 text-xs font-bold rounded-xl py-2.5 bg-emerald-600 text-white disabled:opacity-40">{isOwnerLayout ? "✓ 最终确认布局" : "✓ 批准布局"}</button>
                  <button data-testid="tasklink-layout-changes-toggle" onClick={() => setShowChanges(!showChanges)}
                    className={`flex-1 text-xs font-bold rounded-xl py-2.5 border ${showChanges ? "bg-amber-500 text-white border-amber-500" : "border-amber-300 text-amber-600"}`}>提出修改</button>
                </div>
                {showChanges && (
                  <div className="flex gap-2">
                    <input value={lnote} onChange={(e) => setLnote(e.target.value)} placeholder="修改意见…"
                      className="flex-1 text-xs rounded-xl border border-slate-200 px-3 py-2" data-testid="tasklink-layout-note" />
                    <button data-testid="tasklink-layout-changes-send" disabled={!lnote.trim() || busy} onClick={() => layoutAction("request_changes")}
                      className="text-xs font-bold rounded-xl px-4 bg-amber-500 text-white disabled:opacity-40">提交</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!d.expired && !isLayout && !["done", "closed"].includes(d.refStatus) && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs font-bold text-slate-700 mb-2">{isPunch ? "提交整改结果" : "提交进度 / 完工"}</p>
            <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 py-4 cursor-pointer text-slate-400 hover:border-indigo-300 hover:text-indigo-500" data-testid="tasklink-upload-label">
              {upBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              <span className="text-xs font-semibold">上传照片 / 视频 / 文件（≤20MB）</span>
              <input type="file" multiple accept="image/*,video/*,.pdf" className="hidden" data-testid="tasklink-file-input"
                onChange={(e) => { if (e.target.files?.length) upload(Array.from(e.target.files)); e.target.value = ""; }} />
            </label>
            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tasklink-pending-files">
                {files.map((f, i) => <span key={i} className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5 text-slate-600">📄 {f.filename}</span>)}
              </div>
            )}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="进度说明 / 备注（可选）…"
              className="w-full mt-2 text-xs rounded-xl border border-slate-200 px-3 py-2" data-testid="tasklink-note" />
            {!isPunch && (
              <div className="flex items-center gap-2 mt-2">
                <input type="date" value={sched} onChange={(e) => setSched(e.target.value)} className="text-xs rounded-xl border border-slate-200 px-2.5 py-1.5 flex-1" data-testid="tasklink-schedule-date" />
                <button disabled={!sched || busy} onClick={() => submit("progress", note, { scheduleDate: sched })}
                  className="text-xs font-bold rounded-xl px-3 py-1.5 border border-indigo-300 text-indigo-600 disabled:opacity-40" data-testid="tasklink-schedule-send">提交检验/施工时间</button>
              </div>
            )}
            <div className="flex gap-2 mt-2.5">
              {!isPunch && <button disabled={busy || (!note && files.length === 0)} onClick={() => submit("progress", note)}
                className="flex-1 text-xs font-bold rounded-xl py-2.5 border border-slate-300 text-slate-600 disabled:opacity-40" data-testid="tasklink-submit-progress">{busy ? "提交中…" : "提交进度"}</button>}
              <button disabled={busy || (d.evidenceRequired && files.length === 0 && (d.evidence || []).length === 0)} onClick={() => submit("done", note)}
                className="flex-1 text-xs font-bold rounded-xl py-2.5 bg-emerald-600 text-white disabled:opacity-40" data-testid="tasklink-submit-done">{busy ? "提交中…" : isPunch ? "提交整改完成" : "标记完成"}</button>
            </div>
            {d.evidenceRequired && files.length === 0 && (d.evidence || []).length === 0 && <p className="text-[10px] text-slate-400 mt-1">需先上传证据文件才能标记完成</p>}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs font-bold text-slate-700 mb-2">与项目经理的留言</p>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="tasklink-thread">
            {(d.msgs || []).length === 0 && <p className="text-[11px] text-slate-300 text-center py-3">暂无留言</p>}
            {(d.msgs || []).map((m) => (
              <div key={m.id} className={`flex ${m.from === "sub" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.from === "sub" ? "bubble-mine" : "bg-slate-100 text-slate-700"}`}>
                  <p className="text-[10px] opacity-70">{m.authorName} · {String(m.at || "").slice(5, 16).replace("T", " ")}</p>
                  {m.text && <p className="text-xs whitespace-pre-wrap mt-0.5">{m.text}</p>}
                  {(m.files || []).map((f, i) => (
                    (f.contentType || "").startsWith("image/")
                      ? <img key={i} src={`${BACKEND}/api/public/task-link/${token}/file/${f.fileId}`} alt={f.filename} className="mt-1 rounded-lg max-h-40 object-cover" />
                      : <p key={i} className="text-[11px] underline mt-0.5">📄 {f.filename}</p>
                  ))}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          {!d.expired && (
            <div className="flex gap-2 mt-2.5">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="给项目经理留言…"
                onKeyDown={(e) => { if (e.key === "Enter" && msg.trim() && !busy) submit("message", msg); }}
                className="flex-1 text-xs rounded-xl border border-slate-200 px-3 py-2" data-testid="tasklink-msg-input" />
              <button disabled={!msg.trim() || busy} onClick={() => submit("message", msg)}
                className="rounded-xl px-3.5 bg-slate-900 text-white disabled:opacity-40" data-testid="tasklink-msg-send"><Send className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
        <p className="text-center text-[10px] text-slate-300">ALIGCUS 阿里建筑 · 链接有效期至 {fmtD(d.expiresAt)}</p>
      </div>
    </div>
  );
}
